import React, { useMemo } from 'react'
import { Chess } from 'chess.js'
import { PieceSvg } from './PieceSvg'

interface CapturedPiecesProps {
  fen: string
  className?: string
  /** Show only one color (single row, white left / black right) */
  filter?: 'white' | 'black'
  /** Arena mode: two rows by player perspective. Top = opponent's captures (your pieces taken), bottom = your captures (pieces you took). */
  playerColor?: 'white' | 'black' | null
  /** In arena mode, render board between the two rows so bottom player sees his eaten pieces below the board. */
  children?: React.ReactNode
}

/** Light blue background for captured pieces area so pieces are clear in both light and dark mode. */
const CAPTURED_ROW_BG = 'bg-sky-100'
const CAPTURED_ROW_BORDER = 'border-sky-200'

const ROW_HEIGHT = '2.5rem'
const PIECE_SIZE = 26

export const CapturedPieces: React.FC<CapturedPiecesProps> = ({ fen, className = '', filter, playerColor, children }) => {
  const captured = useMemo(() => {
    try {
      const c = new Chess(fen)
      const initial: Record<string, number> = {
        wP: 8, wR: 2, wN: 2, wB: 2, wQ: 1, wK: 1,
        bP: 8, bR: 2, bN: 2, bB: 2, bQ: 1, bK: 1,
      }
      const current: Record<string, number> = {
        wP: 0, wR: 0, wN: 0, wB: 0, wQ: 0, wK: 0,
        bP: 0, bR: 0, bN: 0, bB: 0, bQ: 0, bK: 0,
      }
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 8; j++) {
          const square = String.fromCharCode(97 + j) + (8 - i)
          const piece = c.get(square as any)
          if (piece) {
            const key = `${piece.color}${piece.type.toUpperCase()}`
            current[key] = (current[key] || 0) + 1
          }
        }
      }
      const white: string[] = []
      const black: string[] = []
      ;(['P', 'R', 'N', 'B', 'Q'] as const).forEach((t) => {
        const w = initial[`w${t}`] - (current[`w${t}`] || 0)
        const b = initial[`b${t}`] - (current[`b${t}`] || 0)
        for (let i = 0; i < w; i++) white.push(`w${t}`)
        for (let i = 0; i < b; i++) black.push(`b${t}`)
      })
      return { white, black }
    } catch {
      return { white: [], black: [] }
    }
  }, [fen])

  const showWhite = !filter || filter === 'white'
  const showBlack = !filter || filter === 'black'

  // Arena mode: two rows – top = opponent's captures (your pieces taken), bottom = your captures (opponent's color; bottom player sees his eaten pieces).
  if (playerColor != null) {
    // Your captures = pieces you took = opponent's color. Opponent's captures = your pieces they took = your color.
    const opponentCaptured = playerColor === 'white' ? captured.white : captured.black // your color (pieces they took)
    const yourCaptured = playerColor === 'white' ? captured.black : captured.white     // opponent color (pieces you took)

    const rowClass = `flex items-center gap-0.5 overflow-x-auto overflow-y-hidden justify-start px-2 rounded border ${CAPTURED_ROW_BG} ${CAPTURED_ROW_BORDER}`

    // DOM order is [your row][board][opponent row] so that with flex-col-reverse the visual order is opponent(top) → board → your(bottom). Ensures bottom row always shows your captures (opponent color).
    return (
      <div className={`flex flex-col-reverse shrink-0 w-full gap-1.5 ${className}`} aria-label="Captured pieces">
        {/* First in DOM → with flex-col-reverse appears at bottom: your captures (opponent color) */}
        <div
          className={`${rowClass} rounded-b border-t-0`}
          style={{ minHeight: ROW_HEIGHT, height: ROW_HEIGHT }}
        >
          {yourCaptured.map((piece, i) => (
            <span key={`you-${i}`} className="shrink-0 flex items-center justify-center">
              <PieceSvg piece={piece as any} size={PIECE_SIZE} />
            </span>
          ))}
        </div>
        {/* Board (when children provided) */}
        {children != null ? children : null}
        {/* Last in DOM → with flex-col-reverse appears at top: opponent's captures (your color) */}
        <div
          className={`${rowClass} rounded-t border-b-0`}
          style={{ minHeight: ROW_HEIGHT, height: ROW_HEIGHT }}
        >
          {opponentCaptured.map((piece, i) => (
            <span key={`opp-${i}`} className="shrink-0 flex items-center justify-center">
              <PieceSvg piece={piece as any} size={PIECE_SIZE} />
            </span>
          ))}
        </div>
      </div>
    )
  }

  // Single row: white left, black right (fixed height). If children (board) provided, render after the row.
  const singleRowBg = `rounded ${CAPTURED_ROW_BG} border ${CAPTURED_ROW_BORDER}`
  return (
    <div className={`flex flex-col shrink-0 w-full ${className}`}>
      <div
        className={`flex justify-between items-center gap-2 overflow-hidden px-2 ${singleRowBg}`}
        style={{ minHeight: ROW_HEIGHT, height: ROW_HEIGHT }}
        aria-label="Captured pieces"
      >
        <div className="flex items-center gap-0.5 overflow-x-auto overflow-y-hidden min-h-[2rem] min-w-0 flex-1 justify-start">
          {showWhite && captured.white.map((piece, i) => (
            <span key={`w-${i}`} className="shrink-0 flex items-center justify-center">
              <PieceSvg piece={piece as any} size={PIECE_SIZE} />
            </span>
          ))}
        </div>
        <div className="flex items-center gap-0.5 overflow-x-auto overflow-y-hidden min-h-[2rem] min-w-0 flex-1 justify-end">
          {showBlack && captured.black.map((piece, i) => (
            <span key={`b-${i}`} className="shrink-0 flex items-center justify-center">
              <PieceSvg piece={piece as any} size={PIECE_SIZE} />
            </span>
          ))}
        </div>
      </div>
      {children}
    </div>
  )
}
